/** A failed API call; `code` is one of the shared ErrorCode values when the server sent one. */
export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, details) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
        this.name = 'ApiError';
    }
}
/** Low-level JSON over HTTP. Cookies are always sent so web sessions can refresh. */
export function createHttp({ baseUrl, fetch: fetchFn = globalThis.fetch.bind(globalThis) }) {
    return async function request(method, path, options = {}) {
        let response;
        try {
            response = await fetchFn(`${baseUrl}${path}`, {
                method,
                credentials: 'include',
                headers: {
                    accept: 'application/json',
                    ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
                    ...(options.token ? { authorization: `Bearer ${options.token}` } : {}),
                },
                ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
            });
        }
        catch (error) {
            throw new ApiError(0, 'NETWORK_ERROR', error instanceof Error ? error.message : 'Network error');
        }
        if (response.status === 204)
            return undefined;
        const body = (await response.json().catch(() => null));
        if (!response.ok) {
            const error = (body ?? {});
            throw new ApiError(response.status, error.code ?? 'HTTP_ERROR', error.message ?? `HTTP ${response.status}`, error.details);
        }
        return body;
    };
}
/** Calls that need no session: the sign-in steps and refresh. */
export function authCalls(http) {
    return {
        login: (body) => http('POST', '/auth/login', { body }),
        verifyTwoFactor: (body) => http('POST', '/auth/2fa', { body }),
        resendTwoFactor: (challengeToken) => http('POST', '/auth/2fa/resend', { body: { challengeToken } }),
        /** Web: no argument, the cookie is used. Desktop/mobile: pass the stored refresh token. */
        refresh: (refreshToken) => http('POST', '/auth/refresh', refreshToken ? { body: { refreshToken } } : {}),
        logout: (token) => http('POST', '/auth/logout', { token }),
        me: (token) => http('GET', '/me', { token }),
    };
}
function queryString(query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query))
        if (value !== undefined)
            params.set(key, String(value));
    const text = params.toString();
    return text ? `?${text}` : '';
}
/** Calls made on behalf of the signed-in user; `authed` adds the token and retries once on 401. */
export function apiCalls(authed) {
    return {
        channels: () => authed('GET', '/channels'),
        channel: (id) => authed('GET', `/channels/${id}`),
        createChannel: (body) => authed('POST', '/channels', body),
        updateChannel: (id, body) => authed('PATCH', `/channels/${id}`, body),
        archiveChannel: (id) => authed('DELETE', `/channels/${id}`),
        /** For good: messages, files and all; needs `delete_channel` like archiving. */
        deleteChannel: (id) => authed('DELETE', `/channels/${id}/permanent`),
        setNotifyLevel: (id, level) => authed('PUT', `/channels/${id}/notify`, { level }),
        removeChannelLogo: (id) => authed('DELETE', `/channels/${id}/logo`),
        members: (id) => authed('GET', `/channels/${id}/members`),
        addMembers: (id, body) => authed('POST', `/channels/${id}/members`, body),
        removeMember: (id, userId) => authed('DELETE', `/channels/${id}/members/${userId}`),
        /** The channel's roles, who holds which, and the default (docs/plans/2026-11-03-channel-roles-design.md). */
        roles: (id) => authed('GET', `/channels/${id}/roles`),
        createRole: (id, body) => authed('POST', `/channels/${id}/roles`, body),
        updateRole: (id, roleId, body) => authed('PATCH', `/channels/${id}/roles/${roleId}`, body),
        removeRole: (id, roleId) => authed('DELETE', `/channels/${id}/roles/${roleId}`),
        /** One step up or down the list (both it and its neighbour must rank below you). */
        moveRole: (id, roleId, direction) => authed('POST', `/channels/${id}/roles/${roleId}/move`, { direction }),
        /** Replaces someone's roles in the channel. */
        setMemberRoles: (id, userId, roleIds) => authed('PUT', `/channels/${id}/members/${userId}/roles`, { roleIds }),
        setDefaultRole: (id, roleId) => authed('PUT', `/channels/${id}/default-role`, { roleId }),
        /** Public channels: leaving takes one off your list until you join again; removing someone does the same to them. */
        leftChannels: () => authed('GET', '/channels/left'),
        joinChannel: (id) => authed('POST', `/channels/${id}/join`),
        /** Archived channels the caller may open again (their roles there allow deleting it). */
        archivedChannels: () => authed('GET', '/channels/archived'),
        restoreChannel: (id) => authed('POST', `/channels/${id}/restore`),
        users: () => authed('GET', '/users'),
        sessions: () => authed('GET', '/auth/sessions'),
        endSession: (id) => authed('DELETE', `/auth/sessions/${id}`),
        voiceToken: (channelId) => authed('POST', `/voice/${channelId}/token`),
        /** Moderators (`voice_moderate`): mute someone's mic, or take them out of the call. */
        muteInCall: (channelId, userId) => authed('POST', `/voice/${channelId}/participants/${userId}/mute`),
        removeFromCall: (channelId, userId) => authed('DELETE', `/voice/${channelId}/participants/${userId}`),
        /** "Görüşmeyi sonlandır": everyone out, for those with voice_moderate. */
        endCall: (channelId) => authed('DELETE', `/voice/${channelId}`),
        openDm: (userId) => authed('POST', '/dm', { userId }),
        messages: (channelId, query = {}) => authed('GET', `/channels/${channelId}/messages${queryString(query)}`),
        sendMessage: (channelId, body) => authed('POST', `/channels/${channelId}/messages`, body),
        editMessage: (id, content) => authed('PATCH', `/messages/${id}`, { content }),
        deleteMessage: (id) => authed('DELETE', `/messages/${id}`),
        react: (id, emoji, on) => authed(on ? 'PUT' : 'DELETE', `/messages/${id}/reactions/${encodeURIComponent(emoji)}`),
        pin: (id, on) => authed(on ? 'PUT' : 'DELETE', `/messages/${id}/pin`),
        pins: (channelId) => authed('GET', `/channels/${channelId}/pins`),
        markRead: (channelId, messageId) => authed('POST', `/channels/${channelId}/read`, { messageId }),
        readStates: () => authed('GET', '/read-states'),
        /** A thread: its first message and a page of replies (docs/plans/2026-11-04-threads-design.md). */
        thread: (rootId, query = {}) => authed('GET', `/threads/${rootId}${queryString(query)}`),
        /** The threads you follow, with their unread replies ("Dallar"). */
        threads: () => authed('GET', '/threads'),
        markThreadRead: (rootId, messageId) => authed('POST', `/threads/${rootId}/read`, { messageId }),
        followThread: (rootId, on) => authed(on ? 'PUT' : 'DELETE', `/threads/${rootId}/follow`),
        /** The phone's push token (design §11); again whenever it or the phone's Do Not Disturb changes. */
        registerDevice: (body) => authed('PUT', '/devices', body),
        removeDevice: (token) => authed('DELETE', '/devices', { token }),
        /** Jira (docs/plans/2026-11-04-jira-design.md): whether it is set up, and the person's own connection. */
        jira: () => authed('GET', '/integrations/jira'),
        /** An Atlassian address to open in a browser; the connection arrives as `jira.connection`. */
        connectJira: () => authed('POST', '/integrations/jira/connect'),
        disconnectJira: () => authed('DELETE', '/integrations/jira/connection'),
        /** Card actions, as the person's own Jira account (409 JIRA_NOT_CONNECTED without one). */
        /** An issue in full (description, comments, fields), as the person sees it in Jira. */
        jiraIssue: (key) => authed('GET', `/integrations/jira/issues/${encodeURIComponent(key)}`),
        jiraTransitions: (key) => authed('GET', `/integrations/jira/issues/${encodeURIComponent(key)}/transitions`),
        jiraTransition: (key, transitionId) => authed('POST', `/integrations/jira/issues/${encodeURIComponent(key)}/transitions`, { transitionId }),
        jiraAssign: (key, accountId) => authed('PUT', `/integrations/jira/issues/${encodeURIComponent(key)}/assignee`, { accountId }),
        jiraAssignable: (key, query) => authed('GET', `/integrations/jira/issues/${encodeURIComponent(key)}/assignable?query=${encodeURIComponent(query)}`),
        jiraComment: (key, text) => authed('POST', `/integrations/jira/issues/${encodeURIComponent(key)}/comments`, { text }),
        /** "Benim işlerim": open issues by list, or a project's open sprint (`project`), from the person's own Jira. */
        jiraMine: (list, project, sprint) => authed('GET', `/integrations/jira/mine?list=${list}${project ? `&project=${encodeURIComponent(project)}` : ''}${sprint ? `&sprint=${sprint}` : ''}`),
        /** How many of the person's own issues are done in the open sprints (one project or all): the progress bar. */
        jiraDone: (list, project) => authed('GET', `/integrations/jira/mine/done?list=${list}${project ? `&project=${encodeURIComponent(project)}` : ''}`),
        jiraSprints: (project) => authed('GET', `/integrations/jira/sprints?project=${encodeURIComponent(project)}`),
        /** A new Jira issue as the person; from a message its thread says so, from /jira the channel does. */
        createJiraIssue: (body) => authed('POST', '/integrations/jira/issues', body),
        /** A channel's Jira feeds; changing them needs "edit channel" and the project visible in the person's Jira. */
        jiraProjects: () => authed('GET', '/integrations/jira/projects'),
        jiraSubscriptions: (channelId) => authed('GET', `/channels/${channelId}/jira-subscriptions`),
        addJiraSubscription: (channelId, body) => authed('POST', `/channels/${channelId}/jira-subscriptions`, body),
        removeJiraSubscription: (channelId, id) => authed('DELETE', `/channels/${channelId}/jira-subscriptions/${id}`),
        /** A call's last two minutes of levels and events, sent by the user to chase a sound problem. */
        sendDiagnostics: (body) => authed('POST', '/diagnostics', body),
        search: (query) => authed('GET', `/search${queryString(query)}`),
        /** Photos and videos, other files or links shared in a channel; newest first, a page at a time. */
        channelMedia: (channelId, query) => authed('GET', `/channels/${channelId}/media${queryString(query)}`),
        /** Fresh signed links once the old ones have expired. */
        attachmentLink: (id) => authed('GET', `/attachments/${id}/link`),
    };
}
//# sourceMappingURL=api.js.map